import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import * as yup from "yup";

import BaseErrors from "@components/fields/BaseErrors";
import FormButtons from "@components/fields/FormButtons";
import RichTextArea from "@components/fields/RichTextArea";
import Form from "@components/forms/Form";
import {
  MyStoryLeadCreateDocument,
  MyStoryLeadCreateMutation,
  MyStoryLeadCreateMutationVariables
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";

type FormData = {
  message: string;
};

type Props = {
  autofocus: boolean;
  journeyId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

const MyStoryLeadForm = ({ autofocus, journeyId, onCancel, onSuccess }: Props) => {
  const defaultValues: FormData = {
    message: ""
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createMyStoryLead] = useMutation<MyStoryLeadCreateMutation, MyStoryLeadCreateMutationVariables>(
    MyStoryLeadCreateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      message: yup.string().required(intl.formatMessage({ id: "errors.myStoryLead.message.blank" }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return createMyStoryLead({
      variables: {
        ...form.getValues()
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.myStoryLeadCreate,
        intl,
        onSuccess,
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.myStoryLead"
      });
    });
  }, [createMyStoryLead, form, intl, onSuccess]);

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <RichTextArea<FormData>
        autofocus={autofocus}
        control={form.control}
        error={form.formState.errors.message}
        journeyId={journeyId}
        label={intl.formatMessage({ id: "forms.myStoryLead.messageLabel" })}
        name="message"
        placeholder={intl.formatMessage({ id: "forms.myStoryLead.messagePlaceholder" })}
        required
        sharableURLSWithoutJourneyId
        toolbarHidden
      />

      <FormButtons hookForm={form} onCancel={onCancel} saveKey="dictionary.submit" />
    </Form>
  );
};

export default MyStoryLeadForm;
